<template>
    <v-container class="my-5">
        <v-layout row wrap class="justify-center">
            <v-flex xs12>
                <!-- Header -->
                <v-card-title class="mb-4">
                    <v-btn icon @click="$router.go(-1)" class="mr-3">
                        <v-icon>mdi-arrow-left</v-icon>
                    </v-btn>
                    <div>
                        <span class="headline font-weight-bold">Plan de Entrenamiento</span>
                        <div class="caption grey--text" v-if="assignment">{{ assignment.member.name }}</div>
                    </div>
                </v-card-title>

                <v-progress-linear v-if="loading" indeterminate class="mb-4"></v-progress-linear>
                <v-alert v-if="errorMessage" type="error" dense outlined>{{ errorMessage }}</v-alert>

                <v-row v-if="assignment" class="mb-4">
                    <!-- Información de la asignación -->
                    <v-col cols="12" md="8">
                        <v-card class="elevation-1">
                            <v-card-title>
                                <v-icon left>mdi-dumbbell</v-icon>
                                {{ assignment.routine_name }}
                            </v-card-title>
                            <v-card-text>
                                <div class="mb-3" v-if="routine && routine.description">
                                    <div class="subtitle-2 font-weight-bold mb-1">Descripción</div>
                                    <div class="body-2">{{ routine.description }}</div>
                                </div>
                                <v-divider class="my-3" v-if="routine && routine.description"></v-divider>
                                <v-row>
                                    <v-col cols="12" sm="6">
                                        <div class="caption grey--text">Coach asignado</div>
                                        <div class="body-2 font-weight-bold">
                                            {{ assignment.coach ? assignment.coach.name : 'Sin coach' }}
                                        </div>
                                    </v-col>
                                    <v-col cols="12" sm="6">
                                        <div class="caption grey--text">Estado</div>
                                        <v-chip small :color="statusInfo(assignment.status).color" text-color="white">
                                            {{ statusInfo(assignment.status).text }}
                                        </v-chip>
                                    </v-col>
                                </v-row>
                                <v-row class="mt-2">
                                    <v-col cols="12" sm="6">
                                        <div class="caption grey--text">Inicio</div>
                                        <div class="body-2">{{ formatDate(assignment.start_date) }}</div>
                                    </v-col>
                                    <v-col cols="12" sm="6" v-if="assignment.end_date">
                                        <div class="caption grey--text">Finalización</div>
                                        <div class="body-2">{{ formatDate(assignment.end_date) }}</div>
                                    </v-col>
                                </v-row>
                                <v-alert v-if="assignment.notes" dense outlined type="info" class="mt-3 mb-0">
                                    <strong>Notas del coach:</strong> {{ assignment.notes }}
                                </v-alert>
                            </v-card-text>
                        </v-card>
                    </v-col>

                    <!-- Resumen de progreso -->
                    <v-col cols="12" md="4">
                        <v-card class="elevation-1 text-center">
                            <v-card-text class="pa-4">
                                <div class="display-2 font-weight-bold primary--text mb-2">{{ latestPercent }}%</div>
                                <div class="subtitle-1 grey--text mb-3">{{ latestLabel }}</div>
                                <v-linear-progress :value="latestPercent" color="primary" class="mb-3"></v-linear-progress>
                                <div class="caption" v-if="latest">
                                    {{ latest.completed_sets_count }} de {{ latest.total_sets_count }} series
                                </div>
                                <v-divider class="my-3"></v-divider>
                                <v-row dense>
                                    <v-col cols="6">
                                        <div class="title font-weight-bold">{{ progress.completed_sessions }}</div>
                                        <div class="caption grey--text">Sesiones completadas</div>
                                    </v-col>
                                    <v-col cols="6">
                                        <div class="title font-weight-bold">{{ progress.total_minutes }}</div>
                                        <div class="caption grey--text">Minutos entrenados</div>
                                    </v-col>
                                </v-row>
                                <div class="caption grey--text mt-2" v-if="progress.last_session_at">
                                    Último entrenamiento: {{ formatDate(progress.last_session_at) }}
                                </div>
                            </v-card-text>
                        </v-card>
                    </v-col>
                </v-row>

                <!-- Ejercicios -->
                <v-card class="elevation-1 mb-4" v-if="exercises.length">
                    <v-card-title>
                        <v-icon left>mdi-list-box</v-icon>
                        Ejercicios ({{ exercises.length }})
                        <v-spacer></v-spacer>
                        <span class="caption grey--text" v-if="latest">{{ latestSubtitle }}</span>
                    </v-card-title>
                    <v-divider></v-divider>
                    <v-list dense>
                        <v-list-item v-for="(exercise, idx) in exercises" :key="idx" class="mb-2">
                            <v-card class="w-100 mb-2" :class="exercise.done ? 'green lighten-5' : ''">
                                <v-card-text class="pa-4">
                                    <v-row align="start" class="mb-1">
                                        <v-col cols="auto">
                                            <div class="body-2 font-weight-bold">{{ idx + 1 }}. {{ exercise.exercise_name }}</div>
                                        </v-col>
                                        <v-spacer></v-spacer>
                                        <v-col cols="auto" v-if="latest">
                                            <v-chip small :color="exercise.done ? 'green' : 'grey lighten-2'"
                                                    :text-color="exercise.done ? 'white' : 'grey darken-2'">
                                                {{ exercise.completed }} / {{ exercise.total }} series
                                            </v-chip>
                                        </v-col>
                                    </v-row>
                                    <v-row dense class="mb-1">
                                        <v-col cols="6" md="3">
                                            <div class="caption grey--text">Series</div>
                                            <div class="body-2 font-weight-bold">{{ exercise.total }}</div>
                                        </v-col>
                                        <v-col cols="6" md="3">
                                            <div class="caption grey--text">Repeticiones</div>
                                            <div class="body-2 font-weight-bold">{{ exercise.reps }}</div>
                                        </v-col>
                                        <v-col cols="6" md="3">
                                            <div class="caption grey--text">Peso</div>
                                            <div class="body-2 font-weight-bold">{{ exercise.weight }}</div>
                                        </v-col>
                                        <v-col cols="6" md="3">
                                            <div class="caption grey--text">Descanso</div>
                                            <div class="body-2 font-weight-bold">{{ exercise.rest }}</div>
                                        </v-col>
                                    </v-row>
                                    <v-alert v-if="exercise.notes" dense outlined type="info" class="ma-0">
                                        <strong>Notas:</strong> {{ exercise.notes }}
                                    </v-alert>
                                </v-card-text>
                            </v-card>
                        </v-list-item>
                    </v-list>
                </v-card>

                <!-- Sesiones recientes -->
                <v-card class="elevation-1" v-if="progress.recent_sessions.length">
                    <v-card-title>
                        <v-icon left>mdi-history</v-icon>
                        Sesiones recientes
                    </v-card-title>
                    <v-divider></v-divider>
                    <v-list dense>
                        <v-list-item v-for="s in progress.recent_sessions" :key="s.id">
                            <v-list-item-content>
                                <v-list-item-title>{{ formatDate(s.started_at) }} • {{ sessionStatus(s.status) }}</v-list-item-title>
                                <v-list-item-subtitle>
                                    {{ s.completed_sets_count }} / {{ s.total_sets_count }} series •
                                    {{ Math.round(s.elapsed_seconds / 60) }} min •
                                    {{ s.completed_volume_kg }} kg de volumen
                                </v-list-item-subtitle>
                            </v-list-item-content>
                        </v-list-item>
                    </v-list>
                </v-card>

                <v-card v-if="assignment && !progress.recent_sessions.length" class="text-center pa-6">
                    <v-icon size="48" class="grey--text mb-2">mdi-run</v-icon>
                    <div class="subtitle-1 grey--text">El socio aún no ha entrenado esta rutina</div>
                </v-card>

                <v-card v-if="!loading && !assignment && !errorMessage" class="text-center pa-6">
                    <v-icon size="48" class="grey--text mb-2">mdi-alert-circle</v-icon>
                    <div class="subtitle-1 grey--text">No se encontró la asignación</div>
                    <v-btn color="primary" text @click="$router.go(-1)" class="mt-2">
                        <v-icon left>mdi-arrow-left</v-icon>
                        Volver
                    </v-btn>
                </v-card>
            </v-flex>
        </v-layout>
    </v-container>
</template>

<script>
import { apiErrorMessage, formatDate, getAssignmentProgress, statusInfo } from '@/services/gym'

const SESSION_STATUS = {
  in_progress: 'En curso',
  paused: 'En pausa',
  completed: 'Completada',
  cancelled: 'Cancelada'
}

export default {
  name: 'MemberWorkoutPlan',
  data: () => ({
    loading: false,
    errorMessage: '',
    assignment: null,
    progress: { completed_sessions: 0, total_minutes: 0, last_session_at: null, latest_session: null, recent_sessions: [] }
  }),
  computed: {
    routine () {
      return this.assignment ? this.assignment.routine : null
    },
    latest () {
      return this.progress.latest_session
    },
    latestPercent () {
      return this.latest ? Math.round(this.latest.progress * 100) : 0
    },
    latestLabel () {
      if (!this.latest) return 'Sin entrenamientos'
      return this.latest.status === 'completed' ? 'Última sesión completada' : 'Sesión en curso'
    },
    latestSubtitle () {
      return `Avance de la sesión del ${formatDate(this.latest.started_at)}`
    },
    // Ejercicios planeados (rutina del socio) cruzados con las series completadas de su última sesión
    exercises () {
      if (!this.routine) return []
      const done = {}
      if (this.latest && this.latest.exercises) {
        this.latest.exercises.forEach(e => {
          done[e.order] = e.sets.filter(s => s.is_completed).length
        })
      }
      return this.routine.exercises.map(e => {
        const first = e.sets[0] || {}
        const completed = done[e.order] || 0
        return {
          exercise_name: e.exercise_name,
          notes: e.notes,
          total: e.sets.length,
          completed,
          done: e.sets.length > 0 && completed >= e.sets.length,
          reps: first.reps === null || first.reps === undefined ? '—' : first.reps,
          weight: first.weight ? `${first.weight} kg` : 'Sin peso',
          rest: `${first.rest_seconds === undefined ? 0 : first.rest_seconds} s`
        }
      })
    }
  },
  created () {
    this.load()
  },
  methods: {
    formatDate,
    statusInfo,
    sessionStatus (status) {
      return SESSION_STATUS[status] || status
    },
    async load () {
      const id = this.$route.query.assignment_id
      if (!id) return
      this.loading = true
      try {
        const { data } = await getAssignmentProgress(id)
        this.assignment = data.assignment
        this.progress = data.progress
      } catch (error) {
        this.errorMessage = apiErrorMessage(error)
      } finally {
        this.loading = false
      }
    }
  }
}
</script>

<style scoped>
.w-100 {
  width: 100%;
}
</style>
